import React, { useEffect, useState } from 'react';
import { Phone, Mail, MessageSquare, CheckCircle, MapPin, ArrowRight, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { generateMathCaptcha, validateFormSubmission, generateTimestampToken } from '../utils/antiSpam';
export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    captcha: '',
    formStartTime: Date.now(),
    formToken: generateTimestampToken(),
    honeypot: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [captcha, setCaptcha] = useState(generateMathCaptcha());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Regenerate captcha when the component mounts
  useEffect(() => {
    setCaptcha(generateMathCaptcha());
    setFormData(prev => ({
      ...prev,
      formStartTime: Date.now(),
      formToken: generateTimestampToken()
    }));
  }, []);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async e => {
    e.preventDefault();
    // Prevent double submission
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError('');
    // Validate form with anti-spam checks
    const validation = validateFormSubmission(formData.email, formData.captcha, captcha.answer, formData.honeypot, 'contact', formData.formStartTime);
    if (!validation.valid) {
      setFormError(validation.error || 'Er is een fout opgetreden. Probeer het opnieuw.');
      setIsSubmitting(false);
      // Generate a new captcha if the current one failed
      setCaptcha(generateMathCaptcha());
      return;
    }
    // Create HTML email content
    const emailHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
        <h2 style="color: #CDA735; border-bottom: 2px solid #CDA735; padding-bottom: 10px;">Nieuw contactformulier bericht</h2>
        <p><strong>Naam:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        ${formData.phone ? `<p><strong>Telefoon:</strong> ${formData.phone}</p>` : ''}
        <div style="margin-top: 20px; padding: 15px; background-color: #f7f7f7; border-left: 4px solid #CDA735;">
          <h3 style="margin-top: 0;">Bericht:</h3>
          <p>${formData.message.replace(/\n/g, '<br>')}</p>
        </div>
        <p style="margin-top: 20px; font-size: 12px; color: #777;">Dit bericht is verzonden via het contactformulier op veranovaklussen.nl</p>
      </div>
    `;
    // Create webhook payload
    const webhookPayload = {
      emailSubject: 'Nieuw contactformulier bericht - Veranova Klussen',
      emailTo: 'info@veranovaklussen.nl',
      formType: 'contact',
      formData: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        message: formData.message,
        timestamp: Date.now(),
        formToken: formData.formToken
      },
      emailHTML: emailHTML
    };
    const webhookUrl = 'https://hook.eu2.make.com/e9ytbwhleok4r9szhhz2ltro238af7as';
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      if (!response.ok) {
        throw new Error('Server responded with an error');
      }
      console.log('Contact form webhook sent successfully:', webhookPayload);
      setFormSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormError('Er is een fout opgetreden bij het verzenden. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Generate a new captcha
  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha());
  };
  return <div className="min-h-screen bg-white w-full" data-id="element-301">
      {/* Hero section */}
      <section className="relative -mt-[100px] pt-52 pb-32 bg-gray-900 overflow-hidden" data-id="element-302">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: "url('https://uploadthingy.s3.us-west-1.amazonaws.com/1kkTSutEG3n1jaMh5paBAH/487922126_17848235598441316_7489604228845729630_n.jpg')"
      }} data-id="element-303"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90" data-id="element-304"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-305">
          <div className="max-w-3xl" data-id="element-306">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-307">
              <div className="w-8 h-[2px] bg-[#CDA735]" data-id="element-308"></div>
              <span className="text-sm font-medium text-[#CDA735] uppercase tracking-wider" data-id="element-309">
                Contact
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-310">
              Neem <span className="text-[#CDA735]" data-id="element-311">direct</span> contact met
              ons op
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed" data-id="element-312">
              Heeft u een vraag of wilt u meer informatie? Wij staan voor u
              klaar en helpen u graag verder.
            </p>
          </div>
        </div>
      </section>
      {/* Main content */}
      <section className="py-20 bg-white" data-id="element-313">
        <div className="container mx-auto px-6" data-id="element-314">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-315">
            {/* Contact information and image */}
            <div data-id="element-316">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-317">
                Onze contactgegevens
              </h2>
              <div className="bg-gray-50 rounded-lg p-8 mb-10" data-id="element-318">
                <div className="space-y-6" data-id="element-319">
                  <div className="flex items-start" data-id="element-320">
                    <MapPin className="h-6 w-6 text-[#CDA735] mt-1 flex-shrink-0" data-id="element-321" />
                    <div className="ml-4" data-id="element-322">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-323">
                        Bezoekadres
                      </h3>
                      <p className="text-gray-600" data-id="element-324">
                        Verandalaan 123
                        <br data-id="element-325" />
                        1234 AB Amsterdam
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-326">
                    <Phone className="h-6 w-6 text-[#CDA735] mt-1 flex-shrink-0" data-id="element-327" />
                    <div className="ml-4" data-id="element-328">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-329">
                        Telefoonnummer
                      </h3>
                      <p className="text-gray-600" data-id="element-330">
                        <a href="tel:+31201234567" className="hover:text-[#CDA735] transition-colors" data-id="element-331">
                          020 - 123 45 67
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-332">
                    <Mail className="h-6 w-6 text-[#CDA735] mt-1 flex-shrink-0" data-id="element-333" />
                    <div className="ml-4" data-id="element-334">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-335">
                        E-mailadres
                      </h3>
                      <p className="text-gray-600" data-id="element-336">
                        <a href="mailto:info@veranova.nl" className="hover:text-[#CDA735] transition-colors" data-id="element-337">
                          info@veranova.nl
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-338">
                    <MessageSquare className="h-6 w-6 text-[#CDA735] mt-1 flex-shrink-0" data-id="element-339" />
                    <div className="ml-4" data-id="element-340">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-341">
                        Adviesgesprek
                      </h3>
                      <p className="text-gray-600" data-id="element-342">
                        Liever een uitgebreid adviesgesprek?{' '}
                        <Link to="/adviesgesprek" className="text-[#CDA735] hover:underline" data-id="element-343">
                          Plan hier een afspraak
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Image of workers */}
              <div className="rounded-lg overflow-hidden shadow-lg" data-id="element-344">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bbqzAX3HGa1qV4DnaGXJAq/IMG-20250423-WA0011.jpg" alt="Veranova team aan het werk" className="w-full h-auto object-cover" data-id="element-345" />
                <div className="bg-gray-50 p-4" data-id="element-346">
                  <p className="text-gray-700 text-sm" data-id="element-347">
                    Ons team van experts staat voor u klaar om uw droomproject
                    te realiseren.
                  </p>
                </div>
              </div>
            </div>
            {/* Contact form */}
            <div data-id="element-348">
              {!formSubmitted ? <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-349">
                    Stuur ons een bericht
                  </h2>
                  {formError && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded" data-id="element-350">
                      <p className="font-medium" data-id="element-351">
                        Er is een probleem opgetreden
                      </p>
                      <p data-id="element-352">{formError}</p>
                    </div>}
                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-353">
                    {/* Honeypot field - hidden from real users but bots will fill it */}
                    <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 overflow-hidden" data-id="element-354">
                      <label htmlFor="website" data-id="element-355">Website</label>
                      <input type="text" id="website" name="honeypot" value={formData.honeypot} onChange={handleChange} tabIndex={-1} autoComplete="off" data-id="element-356" />
                    </div>
                    {/* Hidden timestamp token */}
                    <input type="hidden" name="formToken" value={formData.formToken} data-id="element-357" />
                    <input type="hidden" name="formStartTime" value={formData.formStartTime} data-id="element-358" />
                    <div data-id="element-359">
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-2" data-id="element-360">
                        Naam *
                      </label>
                      <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="Uw volledige naam" data-id="element-361" />
                    </div>
                    <div data-id="element-362">
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2" data-id="element-363">
                        E-mailadres *
                      </label>
                      <div className="relative" data-id="element-364">
                        <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="uw@email.nl" data-id="element-365" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-366">
                          <Mail className="h-5 w-5 text-gray-400" data-id="element-367" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-368">
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-2" data-id="element-369">
                        Telefoonnummer
                      </label>
                      <div className="relative" data-id="element-370">
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="06 12345678" data-id="element-371" />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-372">
                          <Phone className="h-5 w-5 text-gray-400" data-id="element-373" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-374">
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2" data-id="element-375">
                        Uw bericht *
                      </label>
                      <textarea id="message" name="message" rows={6} required value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent" placeholder="Waar kunnen we u mee helpen?" data-id="element-376"></textarea>
                    </div>
                    {/* Captcha */}
                    <div className="p-4 bg-gray-50 rounded-lg" data-id="element-377">
                      <label className="block text-gray-700 font-medium mb-2" data-id="element-378">
                        Verificatie *
                      </label>
                      <div className="flex items-center space-x-4" data-id="element-379">
                        <div className="bg-white p-3 rounded border border-gray-300 flex-grow max-w-[180px]" data-id="element-380">
                          <p className="text-gray-700 font-medium" data-id="element-381">
                            {captcha.question}
                          </p>
                        </div>
                        <input type="text" name="captcha" value={formData.captcha} onChange={handleChange} required className="w-20 px-3 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CDA735] focus:border-transparent text-center" placeholder="?" data-id="element-382" />
                        <button type="button" onClick={refreshCaptcha} className="p-2 text-gray-500 hover:text-[#CDA735] transition-colors" aria-label="Vernieuw captcha" data-id="element-383">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-384">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" data-id="element-385" />
                          </svg>
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-gray-500 flex items-center" data-id="element-386">
                        <Shield className="h-4 w-4 mr-1" data-id="element-387" />
                        Anti-spam maatregel
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600" data-id="element-388">
                      Door het formulier te versturen gaat u akkoord met onze
                      privacyvoorwaarden. Wij gebruiken uw gegevens alleen om
                      contact met u op te nemen.
                    </div>
                    <button type="submit" disabled={isSubmitting} className={`w-full bg-[#CDA735] text-white font-medium px-8 py-3.5 rounded-md hover:bg-[#B89429] transition-all shadow-lg hover:shadow-[0_0_20px_rgba(205,167,53,0.3)] flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-389">
                      {isSubmitting ? <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-390">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-391"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-392"></path>
                          </svg>
                          Versturen...
                        </> : <>
                          <span data-id="element-393">Verstuur bericht</span>
                          <ArrowRight className="ml-2 h-5 w-5" data-id="element-394" />
                        </>}
                    </button>
                  </form>
                </> : <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-395">
                  <div className="w-20 h-20 bg-[#CDA735] rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-396">
                    <CheckCircle className="h-10 w-10 text-white" data-id="element-397" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-398">
                    Bedankt voor uw bericht!
                  </h2>
                  <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto" data-id="element-399">
                    We hebben uw bericht ontvangen en nemen zo snel mogelijk
                    contact met u op.
                  </p>
                  <Link to="/" className="inline-flex items-center px-6 py-3 bg-[#CDA735] hover:bg-[#B89429] text-white rounded-md transition-colors" data-id="element-400">
                    <span data-id="element-401">Terug naar home</span>
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-402" />
                  </Link>
                </div>}
            </div>
          </div>
        </div>
      </section>
    </div>;
};